import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canViewUserReport, isCompanyAdminUser, isDepartmentAdmin } from "@shared/utils/AccessControl";
import { useTranslation } from "react-i18next";
import { getAllDepartmentsWhereaboutsSummary, getDepartmentsWhereaboutsSummary } from '@features/reporting/services/ReportingService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import Colours from '@shared/ui/atoms/Colours';
import { WhereaboutsSummaryBarChart } from "./components/WhereaboutsSummaryBarChart";
import { Spacer } from '@shared/ui/atoms/VerticalSpacer';
import moment, { Moment } from "moment";
import { calculateMaxValue, transformWhereaboutsUsage } from "./WhereaboutsReportingPage";
import { WhereaboutsBookingDownloadReportBar } from "./components/WhereaboutsBookingDownloadReportBar";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';

const animatedComponents: any = makeAnimated();

const ALL_DEPARTMENTS = -1;

export function DepartmentWhereaboutsReport() {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);
  const [date, setDate] = useState<Moment>(moment());

  const [reportData, setReportData] = useState<any>([]);
  const [keyTableData, setKeyTableData] = useState<any>([]);
  const [maxYAxisValue, setMaxYAxisValue] = useState(0);

  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const hasAccess = useMemo(() => canViewUserReport(currentUser),[currentUser]);
  const {t} = useTranslation();
  const departments = useSelector(selectAllDepartments);

  const visibleDepartments = useMemo(() => {
    if (isCompanyAdminUser(currentUser)) {
      return departments;
    } else if (isDepartmentAdmin(currentUser) && currentUser?.department) {
      return departments.filter(d => d.id === currentUser?.department?.id);
    } else {
      return [];
    }
  }, [currentUser, departments]);

  const canViewAllDepartments = useMemo(() => isCompanyAdminUser(currentUser), [currentUser]);
  const isAllDepartmentsSelected = selectedDepartmentId === ALL_DEPARTMENTS;

  const departmentOptions = useMemo(() => {
    const options = visibleDepartments.map(d => ({ value: d.id, label: d.name }));
    if (canViewAllDepartments) {
      return [{ value: ALL_DEPARTMENTS, label: t('reports-page.all-departments') }, ...options];
    }
    return options;
  }, [visibleDepartments, canViewAllDepartments, t]);

  useEffect(() => {
    if (canViewAllDepartments) {
      setSelectedDepartmentId(ALL_DEPARTMENTS);
    } else if (visibleDepartments.length === 1) {
      setSelectedDepartmentId(visibleDepartments[0].id);
    }
  }, [visibleDepartments, canViewAllDepartments]);

  const onDepartmentSelected = (department?: number) => {
    setReportData([]);
    if (department) {
      setSelectedDepartmentId(department);
    }
  }

  useEffect(() => {
    if (date && selectedDepartmentId) {
      setReportData([]);
      setLoading(true)
      const summaryRequest = isAllDepartmentsSelected
        ? getAllDepartmentsWhereaboutsSummary(date)
        : getDepartmentsWhereaboutsSummary(date, selectedDepartmentId);
      summaryRequest.then(resp => {
        const optionList = resp.whereaboutsOptionList;
        const whereaboutsMonthlySummaryKeys = Object.keys(resp.whereaboutsUsage);
        const data = whereaboutsMonthlySummaryKeys.map(key => ({
          name: moment(key).format('ddd')[0],
          dateOfMonth: moment(key).format('DD'),
          date: key,
          // @ts-ignore
          ...transformWhereaboutsUsage(optionList, resp.whereaboutsUsage[key])
        }))
        data.sort((a, b) => a.date < b.date ? -1 : 1);
        setReportData(data);
        setKeyTableData(optionList);
        setMaxYAxisValue(calculateMaxValue(resp.whereaboutsUsage));
      }).finally(() => {
        setLoading(false);
      })
    }
  }, [date, selectedDepartmentId]);

  const onDateChange = (date: Moment) => {
    setDate(date);
  }


  return (
    <ReportingContainer>
      {hasAccess &&
        <>
          <Header>
            <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.department-report')}</HeadlineLarge>
            <SpaceBetweenRow>
              <DateFlicker dateScale={DateScale.Month}
                           updateGlobalDate={false}
                           onChange={onDateChange}
                           disabled={loading} />
              <div style={{width: 230, maxWidth: '100%'}}>
                <Select
                  options={departmentOptions}
                  onChange={(item: any) => onDepartmentSelected(item?.value)}
                  value={departmentOptions.find(o => o.value === selectedDepartmentId) ?? null}
                  closeMenuOnSelect={true}
                  styles={REACT_SELECT_DROPDOWN_STYLES}
                  components={{ ...animatedComponents, ClearIndicator: () => null, IndicatorSeparator: () => null }}
                  isMulti={false}
                  isDisabled={loading}
                  placeholder={t('Select...')}
                  className="teamDropDownList"
                />
              </div>
            </SpaceBetweenRow>
          </Header>

          {!!selectedDepartmentId ? <>
            {loading ? <>
              <Center>
                <LoadingSpinner hideBorder={true} />
              </Center>
            </> : <>
              {reportData.length === 0 ? <>
                <Center style={{marginTop: 32}}>
                  <MountedContentCard>
                    <MountedContentCardContent>
                      <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.whereabouts-report.no-date')}</BodyLarge>
                      <BodyRegular>{t('reports-page.whereabouts-report.select-a-date')}</BodyRegular>
                    </MountedContentCardContent>
                  </MountedContentCard>
                </Center>
              </> : <>
                <WhereaboutsSummaryBarChart reportData={reportData}
                                            maxValueOfYAxis={maxYAxisValue}
                                            keyTableData={keyTableData} />
              </>}
            </>}
            <Spacer />
            <WhereaboutsBookingDownloadReportBar selectedDepartmentId={isAllDepartmentsSelected ? undefined : selectedDepartmentId}
                                                 selectedDepartmentName={isAllDepartmentsSelected ? t('reports-page.all-departments') : departments.find(d => d.id === selectedDepartmentId)?.name}
                                                 allDepartments={isAllDepartmentsSelected}/>
          </> : <>
          </>}

        </>
      }
    </ReportingContainer>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
`

const Header = styled.div`
  margin-bottom: 24px;
`
